import { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { useQuery } from '@tanstack/react-query';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/shadcn/sheet';
import { Input } from '@/components/shadcn/input';
import { listCandidates } from '@/services/communityHostService';
import { communityHostKeys, type HostCandidate } from '@/types/communityHost';
import { formatMonthYear } from './hostFormat';

interface AddCoHostSheetProps {
  open: boolean;
  onClose: () => void;
  onSelect: (candidate: HostCandidate) => void;
}

export function AddCoHostSheet({ open, onClose, onSelect }: AddCoHostSheetProps) {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open) {
      setSearchInput('');
      setSearch('');
    }
  }, [open]);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading } = useQuery({
    queryKey: communityHostKeys.candidates(search),
    queryFn: () => listCandidates(search),
    enabled: open,
  });

  const items = data?.items ?? [];

  return (
    <Sheet open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <SheetContent
        side="bottom"
        showCloseButton={false}
        className="rounded-t-3xl border-t border-gray-100 max-h-[90vh] overflow-y-auto px-5 pt-4 pb-8"
      >
        <div className="w-12 h-1 bg-gray-200 rounded-full mx-auto mb-4" />
        <SheetHeader className="text-left mb-4 space-y-1">
          <SheetTitle className="text-lg font-extrabold text-[#0E1726]">Tambah Co-Host</SheetTitle>
          <SheetDescription className="text-xs text-gray-400 font-medium">
            Co-host mendapat semua akses kelola komunitas.
          </SheetDescription>
        </SheetHeader>

        <div className="relative mb-4">
          <Icon
            icon="ph:magnifying-glass-bold"
            className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
          />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Cari nama member"
            className="h-11 rounded-full pl-10 text-sm bg-[#F8FAFC] border-[#E0E6ED]"
          />
        </div>

        <div className="space-y-2.5">
          {isLoading && (
            <p className="text-center text-xs text-gray-400 py-6">Memuat member...</p>
          )}
          {!isLoading && items.length === 0 && (
            <p className="text-center text-xs text-gray-400 py-6">
              Tidak ada member yang bisa diangkat jadi co-host.
            </p>
          )}
          {items.map((item) => (
            <div
              key={item.community_user_id}
              className="flex items-center gap-3 rounded-2xl border border-[#E0E6ED] bg-white px-4 py-3"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-extrabold text-[#0E1726] truncate">{item.name}</p>
                <p className="text-xs text-gray-400 font-medium mt-0.5 truncate">
                  {item.position_label} · Gabung {formatMonthYear(item.join_date)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => onSelect(item)}
                className="w-8 h-8 rounded-full border border-[#FC480D] text-[#FC480D] flex items-center justify-center cursor-pointer hover:bg-orange-50"
                aria-label={`Angkat ${item.name}`}
              >
                <Icon icon="ph:plus-bold" className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}
